/**
 * AIMDv2 – Constants
 *
 * Static configuration: step metadata, dummy/placeholder data for dropdowns,
 * validation thresholds, and localStorage keys.
 * When real API calls are wired in, replace the DUMMY_ arrays with fetched data.
 */

import {
  StepMeta,
  AudienceOption,
  ToneOption,
  AIMDv2WizardState
} from '../types';

// ---------------------------------------------------------------------------
// Step metadata – drives the StepBar and navigation guards
// ---------------------------------------------------------------------------

export const STEP_META: StepMeta[] = [
  { id: 'requirements', label: 'Requirements', showIcon: false },
  { id: 'outcomes', label: 'Goals', showIcon: false },
  { id: 'documents', label: 'Documents', showIcon: false },
  { id: 'preview', label: 'Preview', showIcon: true },
  { id: 'structure', label: 'Structure', showIcon: true },
  { id: 'content', label: 'Lessons', showIcon: true },
  { id: 'members', label: 'People', showIcon: false }
];

// Once the wizard reaches Structure there is no going back — not to the chat
// stages, and not from Content to Structure. Structure and Content can be
// shared with collaborating admins, and stepping back would regenerate or
// orphan work another admin has built on.
const NO_BACK_NAV_FROM = STEP_META.findIndex((s) => s.id === 'structure');

export const canNavigateToStep = (currentStep: number, target: number): boolean =>
  target >= currentStep || currentStep < NO_BACK_NAV_FROM;

// ---------------------------------------------------------------------------
// Validation thresholds
// ---------------------------------------------------------------------------

export const PROGRAM_NAME_MAX_LENGTH = 100;
export const PROGRAM_DESC_MIN_LENGTH = 200;
export const PROGRAM_DESC_MAX_LENGTH = 1000;
export const MIN_REQUIRED_OUTCOMES = 3;
export const OUTCOME_MIN_LENGTH = 20;
export const OUTCOME_MAX_LENGTH = 200;
export const MAX_TONE_SELECTIONS = 3;
export const MAX_AUDIENCE_SELECTIONS = 20;
export const PROGRAM_PREVIEW_MAX_LENGTH = 2000;

// ---------------------------------------------------------------------------
// localStorage key
// ---------------------------------------------------------------------------

export const LOCAL_STORAGE_KEY = 'aimd_v2_wizard_state';

// ---------------------------------------------------------------------------
// Fallback data – Program Audience (used while API response is loading)
// ---------------------------------------------------------------------------

export const DUMMY_AUDIENCE_OPTIONS: AudienceOption[] = [
  { value: 'dept_sales', label: 'Sales', category: 'Department', categoryId: 'dummy_dept' },
  { value: 'dept_design', label: 'Design', category: 'Department', categoryId: 'dummy_dept' },
  { value: 'grp_management', label: 'Management', category: 'Groups', categoryId: 'dummy_grp' },
  { value: 'grp_leads', label: 'Leads', category: 'Groups', categoryId: 'dummy_grp' }
];

// ---------------------------------------------------------------------------
// Dummy data – Program Tone (replace with API response)
// ---------------------------------------------------------------------------

export const DUMMY_TONE_OPTIONS: ToneOption[] = [
  { value: 'professional', label: 'Professional', description: 'Clear, polished, business-appropriate language.' },
  { value: 'practical', label: 'Practical', description: 'Direct, role-focused, action-oriented.' },
  { value: 'guided', label: 'Guided', description: 'Step-by-step, structured, instructional.' },
  { value: 'coaching', label: 'Coaching', description: 'Supportive, reflective, growth-focused.' },
  { value: 'engaging', label: 'Engaging', description: 'Conversational, energetic, easy to follow.' }
];

// ---------------------------------------------------------------------------
// Dummy data – Previously uploaded documents (replace with API response)
// ---------------------------------------------------------------------------

export const DUMMY_PREVIOUS_DOCUMENTS = [
  {
    id: 'prev_doc_1',
    type: 'text' as const,
    title: 'Examples of great onboarding',
    fileTypeLabel: 'TEXT',
    instructions: 'Prioritize our proprietary framework in sections 2 to 4.',
    processingStatus: 'indexed' as const,
    textContent: 'Sample onboarding content...',
    isNew: false,
    selected: false
  }
];

// ---------------------------------------------------------------------------
// Default / initial wizard state (used on first load or reset)
// ---------------------------------------------------------------------------

export const INITIAL_WIZARD_STATE: AIMDv2WizardState = {
  currentStep: 0,
  highestCompletedStep: -1,
  requirements: {
    programName: '',
    programDescription: '',
    audience: [],
    audienceRules: [],
    tone: []
  },
  outcomes: {
    outcomes: [
      { id: crypto.randomUUID(), value: '' },
      { id: crypto.randomUUID(), value: '' },
      { id: crypto.randomUUID(), value: '' }
    ]
  },
  documents: {
    uploadedDocuments: [],
    previousDocuments: []
  },
  preview: { generatedPreview: null },
  structure: { generatedStructure: null },
  content: { generatedContent: null },
  members: { memberIds: [] },
  lastSavedAt: null,
  generationId: null,
  groupId: null
};
